import { AnimatePresence, motion } from 'framer-motion';
import { User, LogOut, ChevronsUpDown, ShieldOff, Lock } from 'lucide-react';
import { NavLink, useLocation, useNavigate, useOutlet } from 'react-router-dom';

import { useAuth } from '../auth/AuthContext';
import { Logo } from '../components/Logo';
import { OfflineBanner } from '../components/ui/OfflineBanner';
import { clientHasTierAccess } from '../lib/packageGating';

import { BottomNav } from './BottomNav';
import { ClientSwitcher } from './ClientSwitcher';
import { BOOKINGS, HOME, QUESTIONNAIRES, REPORT, RESOURCES, WHATS_NEW } from './navItems';
import type { NavItem } from './navItems';
import { Topbar } from './Topbar';

import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sidebar, SidebarInset, SidebarProvider } from '@/components/ui/sidebar';
import { cn } from '@/lib/utils';

/** Sidebar groups Account out of primary nav and into the user menu at the
 * bottom instead — account settings is a destination *inside* the identity
 * control, not a peer of Report/Questionnaires/Bookings. */
const SIDEBAR_GROUPS: { label: string; items: NavItem[] }[] = [
  { label: 'Overview', items: [HOME] },
  { label: 'Coaching', items: [REPORT, QUESTIONNAIRES, RESOURCES] },
  { label: 'Scheduling', items: [BOOKINGS] },
  { label: 'Help', items: [WHATS_NEW] },
];

function SidebarNavLink({ item }: { item: NavItem }): JSX.Element {
  const { to, label, icon: Icon, end, requiredTier } = item;
  const { client } = useAuth();
  const locked = requiredTier !== undefined && !clientHasTierAccess(client, requiredTier);
  return (
    <NavLink
      to={to}
      end={end ?? false}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-2 rounded-xl px-2 py-2 text-xs font-medium transition-colors',
          'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
          'group-data-[state=collapsed]:justify-center group-data-[state=collapsed]:px-0',
          isActive
            ? 'bg-sidebar-accent font-medium text-sidebar-accent-foreground'
            : locked
              ? 'text-sidebar-foreground/40'
              : 'text-sidebar-foreground/70',
        )
      }
    >
      <Icon className="w-4 h-4 shrink-0" />
      <span className="flex-1 group-data-[state=collapsed]:hidden">{label}</span>
      {locked && <Lock className="w-3 h-3 shrink-0 group-data-[state=collapsed]:hidden" />}
    </NavLink>
  );
}

function SidebarUserMenu(): JSX.Element {
  const { client, logout, logoutAll } = useAuth();
  const navigate = useNavigate();
  const initial = client?.firstName?.[0]?.toUpperCase() ?? '?';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-xl text-left hover:bg-sidebar-accent transition-colors group-data-[state=collapsed]:justify-center group-data-[state=collapsed]:px-0"
        >
          <Avatar size="sm" className="bg-sidebar-primary/20 shrink-0">
            <AvatarFallback className="bg-transparent text-sidebar-primary font-display font-medium">
              {initial}
            </AvatarFallback>
          </Avatar>
          <span className="flex-1 min-w-0 group-data-[state=collapsed]:hidden">
            <span className="block text-sm font-medium text-sidebar-foreground truncate">
              {client ? `${client.firstName} ${client.lastName}` : 'Account'}
            </span>
            {client?.email && (
              <span className="block text-xs text-sidebar-foreground/45 truncate">
                {client.email}
              </span>
            )}
          </span>
          <ChevronsUpDown className="w-3.5 h-3.5 text-sidebar-foreground/40 shrink-0 group-data-[state=collapsed]:hidden" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="start" className="w-60">
        <DropdownMenuItem onClick={() => navigate('/app/account')}>
          <User />
          Account
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => void logout()}>
          <LogOut />
          Sign out
        </DropdownMenuItem>
        <DropdownMenuItem variant="destructive" onClick={() => void logoutAll()}>
          <ShieldOff />
          Sign out of all devices
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Collapsible dashboard shell (DEC-0043) — a themed sidebar that collapses
 * to an icon rail on desktop (`lg:`) and becomes a `Sheet` drawer on mobile,
 * toggled from the `Topbar`'s `SidebarTrigger`, grouped into labeled
 * sections with a user menu anchored at the bottom. A sticky `Topbar`
 * (breadcrumb + theme toggle) runs across every breakpoint. */
export function AppShell(): JSX.Element {
  const location = useLocation();
  const { activeMember, switchClient } = useAuth();
  const navigate = useNavigate();
  // `useOutlet()` snapshots the matched route element for this render rather
  // than re-reading it live off router context. With a bare `<Outlet />`
  // inside the keyed `motion.div` below, navigating updates router context
  // immediately, so the *exiting* (still fading out) motion.div re-renders
  // to the *new* page's content before AnimatePresence ever mounts a fresh,
  // properly-animated-in wrapper for it — the new page can end up stuck at
  // the old wrapper's exit styles (invisible) until a hard reload. Freezing
  // the element here keeps the exiting wrapper showing the outgoing page.
  const element = useOutlet();

  return (
    <div className="min-h-screen bg-background">
      <OfflineBanner />

      <SidebarProvider>
        <Sidebar
          header={
            <div>
              <div className="h-16 flex items-center px-4 group-data-[state=collapsed]:justify-center group-data-[state=collapsed]:px-0">
                <div className="group-data-[state=collapsed]:hidden">
                  <Logo inverted />
                </div>
                <div className="hidden group-data-[state=collapsed]:block">
                  <Logo compact />
                </div>
              </div>
              {/* Only renders for guardians with a household. */}
              <div className="px-3 pb-2 empty:hidden">
                <ClientSwitcher />
              </div>
            </div>
          }
          footer={
            <div className="px-3 pt-2 pb-4 border-t border-sidebar-border">
              <SidebarUserMenu />
            </div>
          }
        >
          <nav className="flex flex-col gap-4 px-3 py-2" aria-label="Primary">
            {SIDEBAR_GROUPS.map((group) => (
              <div key={group.label} className="flex flex-col gap-0.5">
                <span className="px-4 pb-1 text-[11px] font-semibold uppercase tracking-wider text-sidebar-foreground/35 group-data-[state=collapsed]:hidden">
                  {group.label}
                </span>
                {group.items.map((item) => (
                  <SidebarNavLink key={item.to} item={item} />
                ))}
              </div>
            ))}
          </nav>
        </Sidebar>

        <SidebarInset>
          <Topbar />
          {activeMember && (
            <div
              role="status"
              className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-accent/40 px-5 lg:px-10 py-2 text-sm"
            >
              <span>
                Viewing{' '}
                <strong className="font-medium">
                  {activeMember.firstName} {activeMember.lastName}
                </strong>{' '}
                <span className="text-muted-foreground">({activeMember.clientCode})</span>
              </span>
              <button
                type="button"
                className="text-xs font-medium underline underline-offset-2 hover:no-underline"
                onClick={() => void switchClient(null).then(() => navigate('/app'))}
              >
                Back to my profile
              </button>
            </div>
          )}
          <main className="flex-1 min-w-0 w-full p-5 lg:p-10 pb-24 lg:pb-10">
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                // Remounts every page when the viewed profile changes, so no
                // component keeps state or queries from the previous profile.
                key={`${activeMember?.clientId ?? 'self'}:${location.pathname}`}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.18, ease: 'easeOut' }}
              >
                {element}
              </motion.div>
            </AnimatePresence>
          </main>
        </SidebarInset>
      </SidebarProvider>

      <BottomNav />
    </div>
  );
}
